import React, { FC } from 'react'
import clsx from 'clsx';
import {KTIcon} from '@/_metronic/helpers'

interface RowProps {
  label: string;
  curr: number;
  prev: number;
  icon: string;
  format?: (value: number) => string
  isLoading?: boolean;
}

type Props = {
  color: string
  title: string;
  label: string;
  value: string;
  rows: RowProps[];
  isLoading: boolean;
}

const RowKpi: FC<RowProps> = ({ label, icon, curr, prev, format, isLoading }) => {
  const evolution = prev !== 0
    ? ((curr - prev) / prev) * 100
    : (curr > 0 ? Infinity : 0);

  let evolutionFormatted: string;

  if (!isFinite(evolution)) {
    evolutionFormatted = 'N/A';
  } else {
    evolutionFormatted = evolution.toFixed(2);
  }

  return (
    <div className='d-flex align-items-center mb-6'>
      <div className='symbol symbol-45px w-40px me-5'>
        <span className='symbol-label bg-lighten'>
          <KTIcon iconName={icon} className='fs-1' />
        </span>
      </div>
      <div className='d-flex align-items-center flex-wrap w-100'>
        <div className='mb-1 pe-3 flex-grow-1'>
          <span className='fs-5 text-gray-800 fw-bold'>
            {label}
          </span>
          <div className='text-gray-500 fw-semibold fs-7'>
            {isLoading ? (
              <i className='spinner-border spinner-border-sm'></i>
            ) : (
              <>
                {format ? format(prev) : prev} 
                <span className={clsx('fw-bold ms-1', {
                  'text-danger': evolution <= 0,
                  'text-success': evolution > 0
                })}>
                  {evolution > 0 && '+'}{evolutionFormatted}%
                </span>
              </>
            )}
          </div>
        </div>
        <div className='d-flex align-items-center'>
          {isLoading ? (
            <i className='spinner-border'></i>
          ) : (
            <>
              <div className='fw-bold fs-5 text-gray-800 pe-1'>
                {format ? format(curr) : curr} 
              </div>
              <KTIcon
                iconName={`arrow-${evolution > 0 ? 'up' : 'down'}`}
                className={clsx('fs-5 ms-1', {
                  'text-success': evolution > 0,
                  'text-danger': evolution <= 0
                })}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const BaseKpi: FC<Props> = ({color, title, label, value, rows, isLoading}) => {
  return (
    <div className="card">
      <div className='card-body p-0'>
        <div className={`px-9 pt-7 card-rounded h-275px w-100 bg-${color}`}>
          <div className='d-flex flex-stack'>
            <h3 className='m-0 text-white fw-bold fs-3'>{title}</h3>
          </div>
          <div className='d-flex flex-column align-items-center text-white pt-8'>
            <span className='fw-semibold fs-7'>{label}</span>
            {isLoading ? (
              <i className='spinner-border'></i>
            ) : (
              <span className='fw-bold fs-2x pt-1'>{value}</span>
            )}
          </div>
        </div>
        <div
          className='shadow-xs card-rounded mx-9 mb-9 px-6 py-9 position-relative z-index-1 bg-body'
          style={{marginTop: '-100px'}}
        >
          {rows.map((r: RowProps, index: number) => (
            <RowKpi
              key={`kpi-${title.toLowerCase()}-${index}`}
              label={r.label}
              icon={r.icon}
              curr={r.curr}
              prev={r.prev}
              format={r.format}
              isLoading={isLoading}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default BaseKpi;
